import React, { useState, useEffect, useRef } from 'react';

import { Grid, Typography, Fab, Tooltip, makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { XGrid } from '@material-ui/x-grid';

import Content from '../Content';
import AddEditValidationRules from './AddEditValidationRules';
import QuickFilter from 'components/QuickFilter';

import { getService } from '../../reducers/service';

const initialsColumns = [
  { field: 'ptsIBRSValidationRuleID', headerName: 'ptsIBRSValidationRuleID', width: 250 },
  { field: 'NIBRSCode', headerName: 'NIBRS Code', width: 200 },
  { field: 'ErrorMessage', headerName: 'Error Message', width: 600 },
  { field: 'Entity', headerName: 'Entity', width: 200 },
  { field: 'Version', headerName: 'Version', width: 200 },
];

const useStyles = makeStyles(theme => ({
  pageWrap: {
    display: 'flex',
    width: '100%',
    backgroundColor: theme.colors.paper,
  },
  searchBar: {
    padding: theme.spacing(1, 0, 2),
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '15px',
  },
  content: {
    flex: 1,
  },
  actions: {
    width: '100%',
    padding: theme.spacing(1, 0, 2),
    textAlign: 'right',
    '& button': {
      marginLeft: theme.spacing(1),
    },
  },
}));

const IBRSValidationRules = () => {
  const service = getService('ptsibrsvalidationrules');
  const xgridRef = useRef();
  const classes = useStyles();

  const [validationRules, setValidationRules] = useState([]);
  const [selected, setSelected] = useState({});
  const [open, setOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [loading, setLoading] = useState(false);
  const [filteredRows, setFilteredRows] = useState([]);

  useEffect(() => {
    const xgrid = xgridRef.current;
    const viewportHeight = window.innerHeight;
    const xgridOffsetTop = xgrid.offsetTop;
    const xgridHeight = viewportHeight - xgridOffsetTop - 30;
    xgrid.style.height = `${xgridHeight}px`;
  }, []);

  useEffect(() => {
    if (validationRules.length === 0) fetchValidationRules();
  }, [validationRules]);

  const addValidationRule = () => {
    setSelected({});
    setOpen(true);
    setDialogType('Add');
  };

  const editValidationRule = () => {
    setOpen(true);
    setDialogType('Edit');
  };

  const deleteValidationRule = () => {
    let id = selected?.ptsIBRSValidationRuleID;

    service
      .remove(id)
      .then(res => {
        setValidationRules([]);
      })
      .catch(err => console.log('Validation Rule delete error: ', err));
  };

  const closeAddEditDialog = () => {
    setOpen(false);
    setSelected({});
    setValidationRules([]);
  };

  const fetchValidationRules = () => {
    setLoading(true);

    service
      .find({
        query: {
          IsDeleted: false,
          $limit: 0,
        },
      })
      .then(response => {
        let rules = response?.data.map((rule, i) => {
          return { ...rule, id: i + 1 };
        });

        setValidationRules(rules);
        setLoading(false);
      })
      .catch(err => console.log(err));
  };

  const renderActions = () => {
    return (
      <div className={classes.actions}>
        <Tooltip title="Add Validation Rule">
          <Fab size="small" color="secondary" onClick={addValidationRule}>
            <AddIcon />
          </Fab>
        </Tooltip>
        <Tooltip title="Edit Validation Rule">
          <Fab
            size="small"
            color="secondary"
            onClick={editValidationRule}
            disabled={Object.keys(selected).length === 0}>
            <EditIcon />
          </Fab>
        </Tooltip>
        <Tooltip title="Delete Validation Rule">
          <Fab
            size="small"
            color="secondary"
            onClick={deleteValidationRule}
            disabled={Object.keys(selected).length === 0}>
            <DeleteIcon />
          </Fab>
        </Tooltip>
      </div>
    );
  };

  return (
    <Content>
      <div className={classes.pageWrap}>
        <Grid container style={{ padding: 10 }} spacing={2}>
          <Grid xs={12} item>
            <Typography variant="h4">IBRS Validation Rules</Typography>
          </Grid>
          <Grid xs={12} item>
            <div className={classes.content}>
              <div className={classes.searchBar}>
                <QuickFilter
                  rows={validationRules}
                  columns={initialsColumns}
                  setFilteredRows={setFilteredRows}
                  currentService={'ptsibrsvalidationrules'}
                />
                {renderActions()}
              </div>
            </div>
          </Grid>
          <Grid
            ref={xgridRef}
            xs={12}
            item
            style={{
              boxSizing: 'border-box',

              width: '100%',
            }}>
            <XGrid
              columns={initialsColumns}
              rows={filteredRows}
              rowHeight={38}
              disableMultipleSelection
              onRowClick={row => setSelected(row.row)}
              loading={loading}
            />
          </Grid>
        </Grid>
      </div>

      {open && (
        <AddEditValidationRules
          open={open}
          closeAddEditDialog={closeAddEditDialog}
          dialogType={dialogType}
          selectedRule={selected}
          allRules={validationRules}
        />
      )}
    </Content>
  );
};

export default IBRSValidationRules;
